import React, { useCallback, useEffect, useState } from 'react';
import Label from 'components/MeasurementsLabel/MeasurementsLabel';
import { useCore } from 'contexts/core';
import {
  HelpLinesComponentVisible,
  HelpLinesComponentEditMode,
  HelpLinesComponentShape,
  HelpLinesSystem,
  LabelPosition,
  MeasurementsEnum,
  ShapeTypeEnum
} from '@inter-ikea-storageone/range';
import { useDispatch, useSelector } from 'react-redux';
import { helpLinesLabelsVisible, setShape } from 'ducks/helpLines';
import { isRoomConfigurationActive } from 'ducks/layout';
import { reportSlantedWallShapeSizeChange } from 'analytics/reporters';
import { adjustMeasurementsSystem } from 'utils/adjustMeasurementsSystem';
import { invalidateVpcCode } from 'ducks/vpc';
import { useSceneHandler } from 'contexts/sceneHandler';
import { rangeIsPax } from 'utils/queryUtils';
import { useTranslations } from 'hooks/useTranslations';
import { resetDesignId } from 'ducks/designId';
import { useServiceSettings } from 'contexts/serviceSettings';

const HelpLinesWidget = () => {
  const coreApi = useCore();
  const [labels, setLabels] = useState<LabelPosition[]>([]);
  const helpLinesSystem = coreApi.getSystem<HelpLinesSystem>('HelpLinesSystem');
  const roomConfiguratorIsActive = useSelector(isRoomConfigurationActive);
  const isHelpLinesLabelsVisible = useSelector(helpLinesLabelsVisible);
  const dispatch = useDispatch();
  const { setLocalWallShape } = useSceneHandler();
  const { useMetric } = useTranslations();
  const { translations } = useServiceSettings();

  const updateLabels = useCallback(() => {
    const helpLineLabels = helpLinesSystem?.getHelpLineLabels();
    if (helpLineLabels) {
      setLabels(helpLineLabels);
    }
  }, [helpLinesSystem]);

  useEffect(() => {
    const fixedUpdate = () => {
      updateLabels();
    };

    const reportShapeSizeChanged = () => {
      if (helpLinesSystem) {
        reportSlantedWallShapeSizeChange({
          shape: helpLinesSystem.currentShapeType,
          dimensions: helpLinesSystem.getCurrentShapePointsForAnalytics()
        });
        dispatch(invalidateVpcCode());
        dispatch(resetDesignId());
        storeWallShape();
      }
    };

    const handleLoadWallShapeFromVpc = (configuration: any) => {
      if (configuration && configuration.wallShape) {
        const helpLinesVisible = configuration.wallShape.type !== ShapeTypeEnum.off;
        const scene = coreApi.entities.getScene();
        scene?.addComponent(new HelpLinesComponentVisible(helpLinesVisible));
        scene?.addComponent(
          new HelpLinesComponentShape(
            configuration.wallShape.type,
            configuration.wallShape.dimensions
          )
        );
        scene?.addComponent(new HelpLinesComponentEditMode(false));

        dispatch(setShape(configuration.wallShape.type));
      }
    };

    const storeWallShape = () => {
      if (!rangeIsPax() && helpLinesSystem) {
        setLocalWallShape({
          type: helpLinesSystem.currentShapeType,
          dimensions: helpLinesSystem.getShapeDimensions()
        });
      }
    };

    // We need to disable and set shape to off when loading VPC starts.
    // When a VPC contains a wall shape and dispatches events with shape
    // the state shape need to be off to trigger useEffects in HelpLines Widget.
    const handleLoadVpc = () => {
      const scene = coreApi.entities.getScene();
      scene?.addComponent(new HelpLinesComponentVisible(false));
      scene?.addComponent(new HelpLinesComponentShape(ShapeTypeEnum.off, []));
      dispatch(setShape(ShapeTypeEnum.off));
    };

    const unsubscribers = [
      coreApi.subscribe('helpLinesUpdated', fixedUpdate),
      coreApi.subscribe('helpLinesShapeResizeFinished', reportShapeSizeChanged),
      coreApi.subscribe('helpLinesShapeChange', storeWallShape),
      coreApi.subscribe('loadWallShapeFromVpc', handleLoadWallShapeFromVpc),
      coreApi.subscribe('loadVpc', handleLoadVpc),
      coreApi.subscribe('reset', () => setLabels([]))
    ];
    return () => {
      unsubscribers.forEach(unsubscribe => {
        unsubscribe();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coreApi, dispatch, updateLabels]);

  if (!coreApi.scene) return <></>;

  if (
    coreApi.scene?.onlyShowMeasurementIds?.length > 0 &&
    !coreApi.scene?.onlyShowMeasurementIds?.includes(MeasurementsEnum.HelpLinesShapeMeasurements)
  ) {
    return <></>;
  }
  const scene = coreApi.entities.getScene();
  const helpLinesEnabled = scene?.getComponent<HelpLinesComponentVisible>(
    HelpLinesComponentVisible.id
  )?.visible;

  return (
    <>
      {helpLinesEnabled &&
        isHelpLinesLabelsVisible &&
        !roomConfiguratorIsActive &&
        labels?.map(({ dim, x, y, rotation, visible, distance }, i) => {
          return (
            <Label
              key={`HelpLine_${i}`}
              dim={dim}
              size={
                distance !== undefined
                  ? adjustMeasurementsSystem(distance, translations, useMetric)
                  : ''
              }
              x={x}
              y={y}
              visible={visible}
              rotation={rotation}
            />
          );
        })}
    </>
  );
};

export default HelpLinesWidget;
